<template>
  <div>
    <Head title="Cuentas por cobrar" />
    
    <!-- Toolbar -->
    <app-toolbar 
      name="Cuentas por cobrar"
      namePlural="Cuentas por cobrar"
      createHref="/payments/"
      :exportButtons="true"
      :hiddenCreate="true"
    >
      <template v-slot:exportArea>
        <div class="flex gap-2">
          <a 
            :href="`/clientsReports/excel`" 
            target="_blank" 
            class="btn-green flex items-center gap-2"
          >
            <i class="fa fa-file"></i>
            <span class="hidden sm:inline">Excel</span>
          </a>
          <a 
            :href="`/clientsReports/pdf`" 
            target="_blank" 
            class="btn-red flex items-center gap-2"
          >
            <i class="fa fa-print"></i>
            <span class="hidden sm:inline">PDF</span>
          </a>
        </div>
      </template>
    </app-toolbar>

    <!-- Buscador -->
    <div class="mb-6 flex justify-between">
      <div class="w-full max-w-md">
        <input
          v-model="form.search"
          type="text"
          placeholder="Buscar por razón social o RIF..."
          class="w-full px-4 py-2 rounded-xl border focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          @input="debouncedSearch"
        >
      </div>
    </div>

    <!-- Dashboard Boxes -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <box-dashboard title="Cuentas por cobrar" class="bg-blue-50">
        <p class="text-center text-3xl font-black text-blue-700">
          ${{ formatNumber(accounts_total) }}
        </p>
      </box-dashboard>

      <box-dashboard title="Deudas" class="bg-red-50">
        <p class="text-center text-3xl font-black text-red-700">
          ${{ formatNumber(debt) }}
        </p>
      </box-dashboard>

      <box-dashboard title="Pedidos Vencidos" class="bg-yellow-50">
        <p class="text-center text-3xl font-black text-yellow-700">
          ${{ formatNumber(expireds) }}
        </p>
      </box-dashboard>
    </div>

    <!-- Clients Table -->
    <div class="bg-white rounded-xl shadow overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left font-bold bg-gray-50">
              <th class="pb-4 pt-6 px-6">Razón social</th>
              <th class="pb-4 pt-6 px-6">Rif</th>
              <th class="pb-4 pt-6 px-6">Pedidos</th>
              <th class="pb-4 pt-6 px-6">Deuda</th>
              <th class="pb-4 pt-6 px-6"></th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="client in filteredClients" 
              :key="client.id"
              class="hover:bg-gray-50"
            >
              <td class="border-t">
                <Link 
                  class="flex items-center px-6 py-4 hover:text-blue-600" 
                  :href="`/clients/payments/${client.id}`" 
                >
                  {{ client.companyName }}
                </Link>
              </td>
              <td class="border-t">
                <Link 
                  class="flex items-center px-6 py-4" 
                  :href="`/clients/payments/${client.id}`" 
                >
                  {{ client.identification }}
                </Link>
              </td>
              <td class="border-t">
                <Link 
                  class="flex items-center px-6 py-4" 
                  :href="`/clients/payments/${client.id}`" 
                >
                  <span class="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    {{ client.orders.length }}
                  </span>
                </Link>
              </td>
              <td class="border-t">
                <Link 
                  class="flex items-center px-6 py-4" 
                  :href="`/clients/payments/${client.id}`" 
                >
                  <span :class="client.balance > 0 ? 'text-red-600 font-medium' : ''">
                    ${{ formatNumber(client.balance) }}
                  </span>
                </Link>
              </td>
              <td class="w-px border-t">
                <Link 
                  class="flex items-center px-4 hover:text-blue-600" 
                  :href="`/clients/payments/${client.id}`" 
                >
                  <icon 
                    name="cheveron-right" 
                    class="w-6 h-6 text-gray-400" 
                  />
                </Link>
              </td>
            </tr>
            <tr v-if="filteredClients.length === 0">
              <td colspan="5" class="py-8 text-center text-gray-500">
                No hay cuentas por cobrar que coincidan con la búsqueda
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <pagination 
      class="mt-6" 
      :links="clients.links" 
    />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '../../Shared/Layout.vue'
import AppToolbar from '../../Shared/AppToolbar.vue'
import BoxDashboard from '@/Shared/BoxDashboard.vue'
import Icon from '@/Shared/Icon'
import Pagination from '@/Shared/Pagination.vue'
import debounce from 'lodash/debounce'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
  name: 'PaymentsIndex',
  
  components: {
    Icon,
    Head,
    AppToolbar, 
    BoxDashboard,
    Link,
    Pagination
  },

  layout: Layout,

  props: {
    clients: Object,
    accounts_total: Number,
    debt: Number,
    expireds: Number
  },

  data() {
    return {
      form: {
        search: '',
      },
    }
  },

  computed: {
    filteredClients() {
      return this.clients.data
    }
  },

  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get('/clients/payments', pickBy(this.form), { 
          preserveState: true 
        })
      }, 300),
    },
  },

  methods: {
    formatNumber(number) {
      return number.toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    debouncedSearch: debounce(function() {
      // Aquí podrías implementar una búsqueda del lado del servidor si lo necesitas
    }, 300)
  }
}
</script>